import { Button, Table } from "react-bootstrap";
import TextLabel from "src/components/extrapage/Page19/TextLabel.js";
import { memberClassDelete } from "src/services/privateApi";
import messages from "src/utils/messages";
import { swalConfirm, swalError, swalSuccess } from "src/utils/swalHelpers";
import MyBsSelect from "src/components/common/FormElements/MyBsSelect";
import createArrayFromSize from "src/utils/creatArrayFromSize";
import MyBsInput from "src/components/common/FormElements/MyBsInput";
import MyBsCheckbox from "src/components/common/FormElements/MyBsCheckbox";
import StudentClassFormTabs from "./StudentClassFormTabs";
import useStudentClass from "src/hooks/useStudentClass";
import generateClassFormData from "src/utils/generateClassFormData";
import MonthLabel from "./MonthLabel";
import deepCopy from "src/utils/deepCopy";
import { useState } from "react";
import useRegularClass from "src/hooks/useRegularClass";
import generateOptions from "src/utils/generateOptions";
import useTuitionFees from "src/hooks/useTuitionFees";
import * as putMemberClasses from "src/services/react-query-apis/put-member-classes";
import TimeRow from "./time-row";
import DayRow from "./day-row";

const StudentClassForm = () => {
  const {
    billingAmount,
    date,
    setReadOnly,
    memberData,
    formList,
    updateFormList,
    formData,
    validateFormData,
    disableForm,
    tableFilterButtonRef,
    getFormFees,
  } = useStudentClass();

  const regularClass = useRegularClass();
  const tuitionFees = useTuitionFees();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const affiliationOptionList = () => {
    const optionList = regularClass.list.map(val => ({
      ...val,
      value: val.id,
      label: val.affiliation_name,
    }));

    return formList[0]?.id ? optionList : [];
  };

  const handleInputChange = (e, customLogic = {}) => {
    const { name, value, checked } = e.target;
    const isCheckbox = e.target.type === "checkbox";

    const newFormData = {
      ...formData,
      [name]: isCheckbox ? (checked ? 1 : 0) : value,
      ...customLogic,
    };

    updateFormList(newFormData, formList, "update");
  };

  const handleAffiliationChange = e => {
    const { value } = e.target;

    const newFormData = generateClassFormData();
    newFormData.id = formData.id;
    newFormData.closed_flag = formData.closed_flag;
    newFormData.regular_class_id =
      value === "no-value" ? null : parseInt(value);
    newFormData.times = tuitionFees.kaisuuOptionsList(
      newFormData.regular_class_id
    )[0]?.value;
    newFormData.processed_date = formData.processed_date;

    updateFormList(newFormData, formList, "update");
  };

  const handleTimesChange = e => {
    const { name, value } = e.target;
    const newFormData = deepCopy(formData);

    createArrayFromSize(5)
      .filter(val => val > parseInt(value))
      .forEach(pos => {
        [
          `day_week_${pos}`,
          `start_time_${pos}`,
          `end_time_${pos}`,
          `class_level_${pos}`,
          `bus_flag_${pos}`,
        ].forEach(key => {
          newFormData[key] = key.includes("bus_flag") ? 0 : null;
        });
      });

    newFormData[name] = parseInt(value);
    updateFormList(newFormData, formList, "update");
  };

  // Using the generalized handler for similar cases
  const handleClassChange = e => {
    handleInputChange(e, { specialStudy: e.target.value === "0" ? 1 : 0 });
  };

  const handleSpecialCheckboxChange = e => {
    handleInputChange(e, { class: e.target.checked ? "0" : "" });
  };

  const handleTableSelectChange = e => {
    handleInputChange(e, {
      [e.target.name]: e.target.value === "no-value" ? null : e.target.value,
    });
  };

  const handleWeekdayChange = handleTableSelectChange;

  const handleTimeChange = handleTableSelectChange;

  const handleKyuukouChange = handleInputChange;

  const handleBusChange = handleInputChange;

  const getClassFees = () => {
    const fees = getFormFees(formData);
    return fees || "";
  };

  const renderRow = (label, renderCellContent, customClassName = "") => {
    const tdClassName = customClassName || "text-center";

    return (
      <tr>
        <th
          className="text-center"
          style={{ width: "145px", whiteSpace: "nowrap" }}
        >
          {label}
        </th>
        {createArrayFromSize(5).map(val => (
          <td key={val} className={tdClassName}>
            {renderCellContent(val)}
          </td>
        ))}
      </tr>
    );
  };

  const memberClassesUpdateMutation =
    putMemberClasses.usePutMemberClassesMutation({
      mutationConfig: {
        onMutate: () => {
          setIsSubmitting(true);
        },
        onSuccess: async data => {
          tableFilterButtonRef.current.click();
          await swalSuccess(data.message);
          setReadOnly(true);
          setIsSubmitting(false);
        },
        onError: async error => {
          await swalError(error.message);
          setIsSubmitting(false);
        },
      },
    });

  const handleDelete = async () => {
    const shouldDelete = await swalConfirm(messages.classDataDeleteConfirm);

    if (shouldDelete) {
      setIsSubmitting(true);
      const payload = {
        class_id: formData.id,
        member_id: memberData.id,
        processed_date: date,
        regular_class_id: formData.regular_class_id,
        member_classes_id: formData.id,
      };

      const { resType, data } = await memberClassDelete(payload);
      if (resType === 1) {
        updateFormList(formData, formList, "delete");
        await swalSuccess(messages.classDataUpdate);
      }
      if (resType === 3) await swalError(messages.contactMessage);

      setIsSubmitting(false);
      setReadOnly(true);
    }
  };

  const handleSave = async () => {
    if (!(await validateFormData())) {
      return null;
    }

    const classData = formList.map(formData => {
      return {
        ...formData,
        processed_date: date,
        billing_amount: billingAmount,
        member_id: memberData.id,
      };
    });

    const payload = putMemberClasses.generatePayload(
      classData,
      memberData.id,
      date
    );

    memberClassesUpdateMutation.mutate(payload);
  };

  const disableButtons = disableForm || isSubmitting;

  const disableNonAffiliationWidgets =
    disableForm ||
    regularClass.isUnassigned(formData.regular_class_id) ||
    isSubmitting;

  const disableRankInputs =
    disableForm ||
    regularClass.isUnassigned(formData.regular_class_id) ||
    isSubmitting;

  return (
    <div key={formData.id} className="row m-0 my-1 p-1 student-class-form">
      <div className="border border-1">
        <div className="row m-0 mt-2 justify-content-between ">
          <div className="col-md-12">
            <div className="row m-0 g-1 align-items-center">
              <div className="col-md-1">処理年月:</div>
              <div className="col-md-1">
                <MonthLabel date={date} />
              </div>
              <div className="col-md-9"></div>
              <div className="col-md-1 px-2 billing-amount-label">
                <TextLabel label="請求額" />
              </div>
            </div>
          </div>
        </div>
        <StudentClassFormTabs disableButtons={disableButtons} />
        <div className="mb-2">
          <div className="col-12 mb-2 border px-2 py-1">
            <div className="col-md-12">
              <div className="row m-0 g-1 align-items-center">
                <div className="col-md-1 text-end">所属:</div>
                <div className="col-md-2 px-2">
                  <MyBsSelect
                    label=""
                    name="regular_class_id"
                    value={formData.regular_class_id}
                    onChange={handleAffiliationChange}
                    placeholder=""
                    disabled={disableForm}
                  >
                    {generateOptions(affiliationOptionList())}
                  </MyBsSelect>
                </div>
                <div className="col-md-3">
                  <div className="d-flex flex-wrap">
                    <div className="col-3 text-end">回数:</div>
                    <div className="col-3 px-2">
                      <MyBsSelect
                        label=""
                        name="times"
                        value={formData.times}
                        onChange={handleTimesChange}
                        placeholder=""
                        disabled={disableNonAffiliationWidgets}
                      >
                        {generateOptions(
                          tuitionFees.kaisuuOptionsList(
                            formData.regular_class_id
                          )
                        )}
                      </MyBsSelect>
                    </div>
                    <div className="col-3 text-end">級:</div>
                    <div className="col-3 px-2 class-rank-input">
                      <MyBsInput
                        label=""
                        type="number"
                        name="class"
                        onChange={handleClassChange}
                        value={formData.class}
                        placeholder=""
                        readOnly={false}
                        disabled={disableRankInputs}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-1 px-2">
                  <MyBsCheckbox
                    label="特習"
                    name="specialStudy"
                    checked={formData.specialStudy}
                    onChange={handleSpecialCheckboxChange}
                    readOnly={false}
                    disabled={disableRankInputs}
                  />
                </div>
                <div className="col-md-1 px-2">
                  <MyBsCheckbox
                    label="休校"
                    name="closed_flag"
                    checked={formData.closed_flag}
                    onChange={handleKyuukouChange}
                    readOnly={false}
                    disabled={disableNonAffiliationWidgets}
                  />
                </div>
                <div className="col-md-2 text-end">授業料:</div>
                <div className="col-md-1 px-2 class-fees-input">
                  <TextLabel label={getClassFees()} />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-12">
            <Table striped bordered hover responsive>
              <thead>
                <tr></tr>
              </thead>
              <tbody>
                {/* pos is not to be confused with index, it starts from 1 */}
                {renderRow("曜日", pos => {
                  let disabled =
                    disableNonAffiliationWidgets || formData.times < pos;

                  return (
                    <DayRow
                      pos={pos}
                      disabled={disabled}
                      formData={formData}
                      handleWeekdayChange={handleWeekdayChange}
                    />
                  );
                })}

                {renderRow("時間", pos => {
                  let disabled =
                    disableNonAffiliationWidgets || formData.times < pos;

                  return (
                    <TimeRow
                      pos={pos}
                      disabled={disabled}
                      formData={formData}
                      handleTimeChange={handleTimeChange}
                    />
                  );
                })}
                {renderRow("送迎バス", pos => {
                  let disabled =
                    disableNonAffiliationWidgets || formData.times < pos;

                  const key = `bus_flag_${pos}`;

                  return (
                    <div className="d-flex justify-content-center">
                      <MyBsCheckbox
                        label=""
                        key={key}
                        name={key}
                        checked={formData[key]}
                        onChange={handleBusChange}
                        readOnly={false}
                        disabled={disabled}
                      />
                    </div>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <div className="col-md-12 student-class-form-buttons">
            <div className="d-flex justify-content-end">
              <Button
                className="ml-10 btn-danger"
                disabled={disableButtons}
                onClick={handleDelete}
              >
                削除
              </Button>
              <Button
                className="ml-10"
                disabled={disableButtons}
                onClick={handleSave}
              >
                保存
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentClassForm;
